<template>
  <LoadingScreen v-if="isLoading" />
</template>

<script>
import { mapActions, mapState } from 'vuex'

import * as Sentry from '@sentry/vue'
import { authentication, app } from '@microsoft/teams-js'
import config from '@/config'
import constant from '@/constant'
import cookie from '@/services/cookie'
import { scheduleShowEnjoyTrialPopup, scheduleShowTrialNotAvailablePopup } from '@/utils/trial-plan.utils'

import Util from '@/util'
import AuthApi from '@/api/auth'
import LoadingScreen from '@/components-v2/common/loading/LoadingScreen.vue'

export default {
  name: 'Auth',
  components: {
    LoadingScreen,
  },
  data: () => {
    return {
      isLoading: true,
      identifyProvider: '',
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
    }),
  },
  async mounted() {
    this.identifyProvider = this.$route.query.provider

    if (this.identifyProvider) {
      this.handleCreateUserUsingIdentityProvider(this.identifyProvider)
      return
    }
    this.$router.replace({ name: 'Login' })
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction', 'setCurrentUserAction']),

    async getTokenAndUserForProvider(provider) {
      let res = {}
      if (provider === constant.identityProviders.GOOGLE) {
        const referrer = cookie.getValue('referrer')
        const source = window.localStorage.getItem(constant.storageKeys.GOOGLE_SIGNIN_SOURCE)
        this.$route.query.referrer = referrer
        this.$route.query.source = source
        this.$route.query.firstPage = Util.getCookie('ahaFirstPage')
        res = await AuthApi.loginWithGoogle(this.$route.query)
      }

      if (provider === constant.identityProviders.SKOLETUBE) {
        const returnedData = this.$route.query.return_data
        res = await this.$http.post(config.api.createSkoletubePresenter, { returnedData })
        res = res.body
      }

      return res
    },

    async handleCreateUserUsingIdentityProvider(provider) {
      try {
        const {
          token,
          isNew,
          user,
        } = await this.getTokenAndUserForProvider(provider)
        if (!token) {
          throw new Error('Token does not exist')
        }
        Util.removeFirstPageCookie()
        Util.setCookieToken(token)
        if (isNew) {
          cookie.setValue('aha-new-user', true)
        }
        const isGuest = cookie.getValue('aha-guest-user')
        if (isGuest) {
          cookie.deleteValue('aha-guest-user')
          if (user && user.id && this.currentUser.id) {
            await this.setCurrentUserAction(user)
          }
        }

        const {
          GOOGLE_SIGNIN_REDIRECT,
          GOOGLE_SIGNIN_SAVE_SPINNER_WHEEL,
          GOOGLE_SIGNIN_SAVE_WORDCLOUD,
        } = constant.storageKeys
        const redirect = window.localStorage.getItem(GOOGLE_SIGNIN_REDIRECT)

        const saveWordcloud = window.localStorage.getItem(GOOGLE_SIGNIN_SAVE_WORDCLOUD)
        const saveSpinnerwheel = window.localStorage.getItem(GOOGLE_SIGNIN_SAVE_SPINNER_WHEEL)
        const googleSource = window.localStorage.getItem(constant.storageKeys.GOOGLE_SIGNIN_SOURCE)
        const isFromMSTeams = Util.getParamValue('ms_teams', googleSource)
        const isTrialLink = window.localStorage.getItem('isTrialLink') == 'true'
        if (isFromMSTeams) {
          await app.initialize()
          return authentication.notifySuccess(token)
        }
        if (isTrialLink) {
          window.localStorage.removeItem('isTrialLink')
          if (user.hasUpgradedToTrial) {
            scheduleShowEnjoyTrialPopup(user)
          } else if (user.plan !== 'trial-plus-monthly') {
            scheduleShowTrialNotAvailablePopup(user)
          }
        }
        if (redirect) {
          window.localStorage.removeItem(GOOGLE_SIGNIN_REDIRECT)
          window.location.href = redirect
        } else if (saveWordcloud) {
          window.localStorage.removeItem(GOOGLE_SIGNIN_SAVE_WORDCLOUD)
          const query = {
            wordcloud: true,
          }
          this.$router.push({
            path: '/save-presentation',
            query,
          })
        } else if (saveSpinnerwheel) {
          window.localStorage.removeItem(GOOGLE_SIGNIN_SAVE_SPINNER_WHEEL)
          const query = {
            spinnerwheel: true,
          }
          this.$router.push({
            path: '/save-presentation',
            query,
          })
        } else {
          this.$router.replace({ name: 'Presentations' })
        }
        // hide some parts of the UI (header, navbar, etc) if user is new
        // and is NOT saving a wordcloud or spinnerwheel
        if (isNew && !saveWordcloud && !saveSpinnerwheel) {
          // user create from signup page and check i'm ready onboarding
          this.$store.state.isComeFromSignupPage = true
        }

        if (config.region !== constant.euRegion) {
          this.trackGA('success', user, isGuest, isNew, provider)

          Util.trackGA4AndMixpanel('login', {
            method: `with ${provider}`,
          })
          Util.ga4Config({
            user_id: user.id,
          })
        }

        if (isNew) {
          window.dataLayer.push({
            event: 'signup',
          })
        }
      } catch (error) {
        Sentry.captureException(error)
        this.trackGA('failed')

        this.handleErrorForProvider(provider)
      }
    },

    handleErrorForProvider(provider) {
      let errorMsgTitle = 'Login error'
      if (provider === constant.identityProviders.GOOGLE) {
        errorMsgTitle = constant.errors.GOOGLE_LOGIN_ERROR
        setTimeout(() => {
          this.$router.replace({ name: 'Login' })
          this.setErrorOnInternalServerAction(null) // close error popup after redirect
        }, 3000)
      }
      if (provider === constant.identityProviders.SKOLETUBE) {
        errorMsgTitle = constant.errors.SKOLETUBE_LOGIN_ERROR
      }
      this.setErrorOnInternalServerAction(errorMsgTitle) // show global error popup in App.vue
      this.isLoading = false
    },

    trackGA(status, data, asGuest, isNew, identifyProvider) {
      const action = status === 'success' ? 'Success' : 'Failed'
      const from = window.localStorage.getItem(constant.storageKeys.GOOGLE_SIGNIN_FROM)

      // Presenter mới toanh chưa có tài khoản trên AhaSlides,
      // nhưng lại click vào “Login With Google” thì nên record thành event Signup ở GA/MixPanel.
      // Ngược lại, nếu presenter đã có tài khoản rồi
      // mà ấn Signup = Google thì sẽ tính là Login event

      if (status === 'success') {
        if (isNew) {
          if (asGuest) {
            Util.mixpanelTracking(
              constant.eventCategories.PRESENTER_REGISTER,
              'Sign up',
              'Demo with Google',
            )
            Util.trackGA4AndMixpanel('sign_up', {
              method: 'Demo with Google',
            })
          } else {
            Util.mixpanelTracking(
              constant.eventCategories.PRESENTER_REGISTER,
              'Sign up',
              `with ${identifyProvider}`,
            )
            Util.trackGA4AndMixpanel('sign_up', {
              method: `with ${identifyProvider}`,
            })
          }
        } else {
          Util.mixpanelTracking(
            constant.eventCategories.PRESENTER_REGISTER,
            'Login',
            `with ${identifyProvider}`,
          )
        }
      } else {
        Util.mixpanelTracking(
          constant.eventCategories.PRESENTER_REGISTER,
          `${from} > ${action}`,
          `with ${identifyProvider}`,
        )
      }
    },
  },
}
</script>
